import type { Experiment, Result } from '@growthbook/growthbook';
import { GrowthBook } from '@growthbook/growthbook';
import { getClickStreamEvent, getEventAnalytic } from 'services/clickstream';
import { FINGERPRINT } from 'store/constants';

import { getMappingChannel, softLocalStorageGet } from '../../utils';
import { fetchWithTimeout } from '../../utils/fetchWithTimeout';
import { getSessionId } from '../../utils/session';
import type { Exp, GBFeaturedParam } from './types';
import { arrStringToNumber } from './utils';

const isDevelopmentMode = process.env.NODE_ENV === 'development';
const clickstream = getEventAnalytic();

declare global {
  interface Window {
    growthbookCashier: GrowthBook;
  }
}

const duration = 800;

interface GBState {
  loading: boolean;
  loaded: boolean;
  failed: boolean;
}

const initialState: GBState = {
  loading: false,
  loaded: false,
  failed: false,
};

let state: GBState = { ...initialState };

const isLoading = () => state.loading;
const isFailed = () => state.failed;
const isInitialized = () => state.loaded;

const reset = () => {
  state = { ...initialState };
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const trackingCallback = (exp: Experiment<any>, result: Result<any>) => {
  try {
    const { key } = exp;
    const { variationId } = result;
    const сlEvent = getClickStreamEvent.growthbook_experiment_start({
      key,
      variationId,
      sessionId: getSessionId(),
    });
    if (clickstream.isInitialized()) {
      clickstream.push(сlEvent);
    }
    if (isDevelopmentMode) {
      console.log('Experiment Viewed', {
        experiment: exp,
        result,
      });
    }
  } catch {
    /* empty */
  }
};

const growthBook = new GrowthBook({
  trackingCallback,
});

const initGBFeatures = async (params: GBFeaturedParam) => {
  const {
    currencyAlpha3,
    merchantUrl,
    userId,
    growthbookFeatures,
    brand,
    userBalance,
    clickstreamId,
    language,
    appChannel,
    platform,
    userAmsSegments,
  } = params;
  const requestUrl = `${merchantUrl}/growthbook/api/features`;
  if (!growthbookFeatures || !currencyAlpha3 || !userId) return;
  const fingerPrint = softLocalStorageGet(FINGERPRINT);

  const attributes = {
    brand,
    currency: currencyAlpha3,
    accountNumber: userId,
    userAgent: navigator.userAgent,
    balanceTotal: userBalance,
    channel: getMappingChannel(appChannel, platform),
    clickstreamId,
    fingerprint: fingerPrint,
    hostName: merchantUrl.replace(/^https?:\/\//i, ''),
    language,
    amsCategories: arrStringToNumber(userAmsSegments),
  };

  growthBook.setAttributes(attributes);

  try {
    state.loading = true;
    await fetchWithTimeout(requestUrl, duration)
      .then((res) => res.json())
      .then(({ features }: Exp) => {
        const featuredKeys: string[] = Object.keys(features);
        const cashierFeatures = featuredKeys.reduce((acc, key) => {
          if (growthbookFeatures?.includes(key)) {
            const exp = features[key as keyof typeof features] as Omit<Experiment<unknown>, 'key'>;
            acc[key] = exp;
          }
          return acc;
        }, {} as Exp);

        growthBook.setPayload({
          features: cashierFeatures,
        });

        state.loaded = true;
        state.loading = false;
        if (!window.growthbookCashier) {
          window.growthbookCashier = growthBook;
        }
      })
      .catch(() => {
        state.failed = true;
        state.loading = false;
      });
    // eslint-disable-next-line no-empty
  } catch {}
};

export const getGBFeatures = () => ({
  isFailed,
  isLoading,
  initGBFeatures,
  isInitialized,
  growthBook,
  reset,
});
